import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {LatestLessonActions} from './action-types';
import {concatMap, filter, map, mergeMap, withLatestFrom} from 'rxjs/operators';
import {LatestLessonsDbService} from '../services/latest-lessons-db.service';
import {latestLessonsPageLoaded} from './latest-lesson.actions';
import {AppState} from './index';
import {select, Store} from '@ngrx/store';
import {selectLatestLessonsState} from './latest-lessons.selectors';
import {UserLessonStatusDbService} from '../services/user-lesson-status.db.service';
import {combineLatest, from, lastValueFrom} from 'rxjs';
import {selectAllCourses, selectUser} from './selectors';
import {userLessonsStatusLoaded} from './user-lesson-status.actions';
import {selectCoursesWithLessonViewedStatusLoaded} from './user-lesson-status.selectors';
import {Router} from '@angular/router';
import {LessonsDBService} from '../services/lessons-db.service';

import uniq from 'uniq';
import {LoadingService} from '../shared/services/loading.service';

@Injectable()
export class LatestLessonEffects {

  loadLatestLessons$ = createEffect(
    () => this.actions$
      .pipe(
        ofType(LatestLessonActions.loadNextLatestLessonsPage, LatestLessonActions.changeLatestLessonsSortOrder),
        withLatestFrom(this.store.pipe(select(selectLatestLessonsState))),
        concatMap(([action, state]) =>
            this.loading.showLoaderUntilCompleted(
              this.latestLessonsDB.loadLatestLessonsPage(
                state.latestLessons.length ? state.latestLessons[state.latestLessons.length -1].lastUpdated : null,
                state.sortOrder)
            )
        ),
        map(latestLessons => latestLessonsPageLoaded({latestLessons})))
  );

  loadLatestLessonsViewedStatusIfNeeded$ = createEffect(
    () => this.actions$
      .pipe(
        ofType(LatestLessonActions.latestLessonsPageLoaded),
        concatMap(action =>  {

          const courseIds = action.latestLessons.map(lesson => lesson.courseId);

          const uniqueCourseIds:string[] =uniq(courseIds);

          return from(uniqueCourseIds);
        }),
        withLatestFrom(this.store.pipe(select(selectCoursesWithLessonViewedStatusLoaded))),
        filter( ([courseId, coursesLoaded]) => !coursesLoaded.find(id => id == courseId) ),
        map(([courseId]) => courseId),
        withLatestFrom(this.store.pipe(select(selectUser))),
        filter(([courseId, user]) => user.id != 'anonymous'),
        mergeMap(([courseId, user]) =>
          this.userLessonsDb.loadLessonsWatchedByCourse(user.id, courseId)
            .pipe(
              map(userLessonsStatusList => [courseId, userLessonsStatusList])
            )
        ),
        map(([courseId, userLessonsStatusList]:any) => userLessonsStatusLoaded({courseId, userLessonsStatusList}))
      )
  );

  navigateToLesson$ = createEffect(
    () => this.actions$
      .pipe(
        ofType(LatestLessonActions.navigateToLesson),
        withLatestFrom(
          this.store.pipe(select(selectAllCourses))
        ),
        concatMap(([action, courses]) => {

          if (!courses || !courses.length) {

            return this.loading.showLoaderUntilCompleted(
              combineLatest([
                this.lessonsDB.loadCourseSection(action.courseId, action.sectionId),
                this.lessonsDB.loadCourse(action.courseId),
              ])
            )
              .pipe(
                map(([section, course]) => {

                  return [action, [course], section]

                })
              )

          }

          return this.loading.showLoaderUntilCompleted(this.lessonsDB.loadCourseSection(action.courseId, action.sectionId))
            .pipe(
              map(section => [action, courses, section])
            )
        }),
        map(async ([action, courses, section]:any) => {

          const course = courses.find(course => course.id == action.courseId);

          let courseSection = section;

          if (course?.cloneType === 'read-only') {
            courseSection = await lastValueFrom(this.lessonsDB.loadCourseSection(course.sourceCourseId, action.sectionId));
          }

          await this.router.navigateByUrl(`/courses/${course.url}/${courseSection.seqNo}/lessons/${action.seqNo}`);

        })
      )
  ,{dispatch:false});


  constructor(
    private actions$ : Actions,
    private latestLessonsDB: LatestLessonsDbService,
    private store: Store<AppState>,
    private loading: LoadingService,
    private userLessonsDb: UserLessonStatusDbService,
    private router:Router,
    private lessonsDB: LessonsDBService) {

  }

}
